import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";

type ApiKeyRevealDialogProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onConfirm: (password: string) => Promise<void>;
	onGoogleVerify?: () => void;
	canUsePasswordAuth: boolean;
	canUseGoogleAuth: boolean;
	loading?: boolean;
	title?: string;
	description?: string;
};

type RevealMethod = "password" | "google";
const LAST_METHOD_STORAGE_KEY = "reveal-key:last-method";

export default function ApiKeyRevealDialog({
	open,
	onOpenChange,
	onConfirm,
	onGoogleVerify,
	canUsePasswordAuth,
	canUseGoogleAuth,
	loading = false,
	title = "Reveal API key",
	description = "Enter your account password to reveal and copy this API key.",
}: ApiKeyRevealDialogProps) {
	const [password, setPassword] = useState("");
	const [error, setError] = useState("");
	const [activeMethod, setActiveMethod] = useState<RevealMethod>("password");

	useEffect(() => {
		if (!open) {
			setPassword("");
			setError("");
		}
	}, [open]);

	useEffect(() => {
		if (!open) return;
		if (canUsePasswordAuth && !canUseGoogleAuth) {
			setActiveMethod("password");
			return;
		}
		if (!canUsePasswordAuth && canUseGoogleAuth) {
			setActiveMethod("google");
			return;
		}
		if (!canUsePasswordAuth && !canUseGoogleAuth) return;
		try {
			const lastMethod = window.localStorage.getItem(LAST_METHOD_STORAGE_KEY);
			if (lastMethod === "google" || lastMethod === "password") {
				setActiveMethod(lastMethod);
				return;
			}
		} catch {
			// Ignore storage failures.
		}
		setActiveMethod("password");
	}, [open, canUseGoogleAuth, canUsePasswordAuth]);

	function rememberMethod(method: RevealMethod) {
		try {
			window.localStorage.setItem(LAST_METHOD_STORAGE_KEY, method);
		} catch {
			// Ignore storage failures.
		}
	}

	async function handleConfirm() {
		if (!password) {
			setError("Password is required");
			return;
		}
		setError("");
		try {
			rememberMethod("password");
			await onConfirm(password);
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to reveal API key");
		}
	}

	function selectMethod(method: RevealMethod) {
		setError("");
		setActiveMethod(method);
	}

	if (!open) return null;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<form
					onSubmit={(event) => {
						event.preventDefault();
						void handleConfirm();
					}}
				>
					<DialogHeader>
						<DialogTitle>{title}</DialogTitle>
						<DialogDescription>{description}</DialogDescription>
					</DialogHeader>
					<Tabs
						value={activeMethod}
						onValueChange={(value) => {
							if (value === "password" || value === "google") {
								selectMethod(value);
							}
						}}
						className="mt-4"
					>
						{canUsePasswordAuth && canUseGoogleAuth ? (
							<TabsList>
								<TabsTrigger value="password" disabled={loading}>
									Password
								</TabsTrigger>
								<TabsTrigger value="google" disabled={loading}>
									Google
								</TabsTrigger>
							</TabsList>
						) : null}

						{canUsePasswordAuth ? (
							<TabsContent value="password" className="mt-4">
								<label className="block text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
									Password
								</label>
								<input
									type="password"
									value={password}
									onChange={(event) => setPassword(event.target.value)}
									autoComplete="current-password"
									className="mt-2 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
									placeholder="Enter your password"
									autoFocus
								/>
								{error ? <p className="mt-2 text-sm text-rose-600">{error}</p> : null}
								<DialogFooter>
									<Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={loading}>
										Cancel
									</Button>
									<Button type="submit" disabled={loading}>
										{loading ? "Revealing..." : "Reveal key"}
									</Button>
								</DialogFooter>
							</TabsContent>
						) : null}

						{canUseGoogleAuth && onGoogleVerify ? (
							<TabsContent value="google" className="mt-4">
								<p className="text-sm text-slate-600">
									Verify with your Google account to continue.
								</p>
								<div className="mt-4 flex items-center justify-end gap-2">
									<Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={loading}>
										Cancel
									</Button>
									<Button
										type="button"
										onClick={() => {
											rememberMethod("google");
											onGoogleVerify();
										}}
										disabled={loading}
									>
										Verify with Google
									</Button>
								</div>
							</TabsContent>
						) : null}
					</Tabs>
				</form>
			</DialogContent>
		</Dialog>
	);
}
